import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { makeStyles } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import Icon from '../../../../../atoms/Icon'
import { usePipelineChat } from './PipelineChatContext'
import { usePipelineWorkspace } from './PipelineWorkspaceContext'
import ChatMessage from './ChatMessage'
import ThreadHeader from './ThreadHeader'

const useStyles = makeStyles(theme => ({
  chatPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'var(--surface-bg-color-d3)'
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column'
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    color: 'var(--color-neutral-400)',
    textAlign: 'center',
    padding: theme.spacing(4)
  },
  emptyStateText: {
    fontSize: 'var(--font-size-sm)',
    fontStyle: 'italic'
  },
  inputArea: {
    borderTop: '1px solid var(--color-neutral-600)',
    padding: theme.spacing(1.5),
    backgroundColor: 'var(--surface-bg-color-d2)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  inputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  textarea: {
    flex: 1,
    minHeight: '36px',
    maxHeight: '96px',
    padding: theme.spacing(0.75, 1),
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-sm)',
    fontFamily: 'var(--root-font)',
    fontSize: 'var(--font-size-sm)',
    lineHeight: '24px',
    resize: 'none',
    backgroundColor: 'var(--surface-bg-color-d1)',
    color: 'var(--text-color-main)',
    overflow: 'hidden',
    '&:focus': {
      outline: 'none',
      borderColor: 'var(--color-primary-500)'
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed'
    }
  },
  loadingIndicator: {
    padding: theme.spacing(0.75, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--font-size-xs)',
    marginTop: theme.spacing(1)
  },
  loadingDots: {
    display: 'flex',
    gap: '4px',
    '& span': {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: 'var(--color-primary-500)',
      animation: '$pulse 1.4s ease-in-out infinite',
      '&:nth-child(2)': {
        animationDelay: '0.2s'
      },
      '&:nth-child(3)': {
        animationDelay: '0.4s'
      }
    }
  },
  '@keyframes pulse': {
    '0%, 80%, 100%': {
      opacity: 0.3,
      transform: 'scale(0.8)'
    },
    '40%': {
      opacity: 1,
      transform: 'scale(1)'
    }
  }
}))

function ChatPanel () {
  const classes = useStyles()
  const {
    messages,
    agentStatus,
    invokeAgent,
    cancelAgent,
    isCancelling,
    buildAttachments
  } = usePipelineChat()

  const { setProposedPipeline } = usePipelineWorkspace()

  const [inputValue, setInputValue] = useState('')
  const textareaRef = useRef(null)
  const messagesEndRef = useRef(null)

  const isProcessing = agentStatus === 'processing' || isCancelling
  const hasMessages = messages && messages.length > 0

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      textareaRef.current.style.height = `${scrollHeight}px`
      textareaRef.current.style.overflowY = scrollHeight > 96 ? 'auto' : 'hidden'
    }
  }, [inputValue])

  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isProcessing) return

    const message = inputValue.trim()
    setInputValue('')
    const attachments = buildAttachments()

    try {
      await invokeAgent(message, attachments)
    } catch (error) {
      console.error('Failed to send message:', error)
    }
  }, [inputValue, isProcessing, buildAttachments, invokeAgent])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend])

  const loadingText = useMemo(() => {
    if (isCancelling) return '◌ Cancelling...'
    for (let i = 0; i < messages.length; i++) {
      const message = messages[i]
      if (message.role === 'assistant' && message.toolCalls) {
        const executePipelineCall = message.toolCalls.find(tc => tc.name === 'execute_pipeline')
        if (executePipelineCall) {
          const nextMessage = messages[i + 1]
          const hasResult = nextMessage && nextMessage.role === 'tool' && (
            nextMessage.toolCallId === executePipelineCall.id ||
            nextMessage.name === executePipelineCall.name
          )
          if (!hasResult) return '▶ Running pipeline...'
        }
      }
    }
    return '◌ Thinking...'
  }, [messages, isCancelling])

  const onProposePipeline = useCallback((pipelineJsonString) => {
    return setProposedPipeline(pipelineJsonString)
  }, [setProposedPipeline])

  return (
    <div className={classes.chatPanel}>
      <ThreadHeader />
      <div className={classes.messagesContainer}>
        {!hasMessages && (
          <div className={classes.emptyState}>
            <div className={classes.emptyStateText}>
              Describe what you'd like to build or change, and I'll help you create the pipeline.
            </div>
          </div>
        )}
        {hasMessages && messages.map((message, idx) => {
          if (message.role === 'tool' && idx > 0) {
            const prevMessage = messages[idx - 1]
            if (prevMessage.role === 'assistant' && prevMessage.toolCalls && prevMessage.toolCalls.length > 0) {
              const isShownInline = prevMessage.toolCalls.some(tc =>
                (message.toolCallId && tc.id === message.toolCallId) ||
                (message.name && tc.name === message.name)
              )
              if (isShownInline) return null
            }
          }
          return (
            <ChatMessage
              key={idx}
              message={message}
              allMessages={messages}
              messageIndex={idx}
              onProposePipeline={onProposePipeline}
            />
          )
        }).filter(Boolean)}
        {isProcessing && (
          <div className={classes.loadingIndicator}>
            <div className={classes.loadingDots}>
              <span />
              <span />
              <span />
            </div>
            <span>{loadingText}</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={classes.inputArea}>
        <div className={classes.inputContainer}>
          <textarea
            ref={textareaRef}
            className={classes.textarea}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Describe what you want to build or change...'
            rows={1}
          />
          <SydButton
            variant={isProcessing ? 'outline' : 'primary'}
            size='sm'
            onClick={isProcessing ? cancelAgent : handleSend}
            disabled={isProcessing ? isCancelling : (!inputValue.trim() || isProcessing)}
          >
            {isProcessing
              ? <Icon name='square' style={{ fontSize: '16px' }} />
              : 'Send'}
          </SydButton>
        </div>
      </div>
    </div>
  )
}

export default ChatPanel
